/**
 * src/shell/ui/screens/CockpitScreen.tsx — S1 驾驶舱主屏（设计稿 §1.2 S1 / §4.3）。
 *
 * 桌面即导航 + 桌面即状态（待办即风景）：
 * - 文件堆高矮 = 待拍板决策数（0=空 / 1–2=矮 / >2=高）
 * - 电话灯呼吸 = 有未读事件（外壳本地 last-read，评审对齐项 3）
 * - 电脑屏幕微光 = 有在研项目且引擎阶段灯非 idle
 * - 卡带架卡带数 = completed 项目数
 * 灰盒期无底图：四物件 = 色块热区，锚点全部读资产清单（MANIFEST，§4.5）。
 * MANIFEST.baseImage 登记后灰盒毕业：色块/常驻标签退场（§4.4 占位规范），
 * 热区与状态覆盖层保留（它们是设计的一部分）。
 */

import { replayEvents } from '../../../gm'
import { MANIFEST, type Anchor } from '../../assets/manifest'
import { COPY } from '../../copy'
import { useRunState, useSave } from '../../gm-client/hooks'
import { lastReadSeq } from '../../save/unread'
import { deskTodoBadge, deskTodoStackCls } from '../desk-todo'
import { StatusBar } from '../components/StatusBar'
import type { Screen } from '../nav'
import type { CSSProperties } from 'react'

function anchorStyle(a: Anchor): CSSProperties {
  return { left: `${a.x}%`, top: `${a.y}%`, width: `${a.w}%`, height: `${a.h}%` }
}

export function CockpitScreen({ go }: { go: (s: Screen) => void }) {
  const { bundle } = useSave()
  const C = COPY.cockpit
  const graduated = MANIFEST.baseImage !== null

  const projects = bundle.projects.projects
  const pendingDecisions = bundle.decisions.decisions.filter((d) => d.status === 'pending')
  const inReview = projects.filter((p) => p.status === 'in_review')
  const completed = projects.filter((p) => p.status === 'completed')
  const active = projects
    .filter((p) => p.status !== 'completed')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]

  const runState = useRunState(active?.id ?? null)
  const engineBusy = !!runState && runState.phase !== 'idle'

  // 未读：在研项目段 + 全局段任一存在未读事件（外壳本地 last-read seq）
  const unread =
    (active ? hasUnreadEvents(active.id) : false) || hasUnreadEvents(null)

  // 待办即风景：待拍板决策 + 待上架卡带共用一个计数（缺陷 C 修复，2026-08-15 拍板）
  const todoCount = pendingDecisions.length + inReview.length
  const stackCls = deskTodoStackCls(todoCount)
  const todoBadge = deskTodoBadge(pendingDecisions.length, inReview.length)

  return (
    <div className="screen cockpit-screen">
      <StatusBar />
      <div className={graduated ? 'desk graduated' : 'desk'}>
        {graduated && (
          <img className="desk-base" src={MANIFEST.baseImage ?? ''} alt="" draggable={false} />
        )}
        {/* 最左：办公电脑 */}
        <button
          type="button"
          className={graduated ? 'desk-object' : 'desk-object graybox'}
          style={anchorStyle(MANIFEST.desk.computer)}
          onClick={() => go({ name: 'computer', tab: 'projects' })}
        >
          <span className="obj-label">{C.objects.computer}</span>
        </button>
        {engineBusy && (
          <span className="screen-glow" style={anchorStyle(MANIFEST.desk.computerScreen)} title="开发进行中" />
        )}

        {/* 中间：文件堆 */}
        <button
          type="button"
          className={`desk-object ${graduated ? '' : 'graybox '}${stackCls}`.trimEnd()}
          style={anchorStyle(MANIFEST.desk.papers)}
          onClick={() => go({ name: 'decisions' })}
        >
          <span className="obj-label">{C.objects.papers}</span>
          {todoCount > 0 ? (
            <span className="paper-stack">
              {Array.from({ length: Math.min(todoCount, 5) }).map((_, i) => (
                <span key={i} className="paper-sheet" />
              ))}
            </span>
          ) : null}
          <span className="obj-badge">{todoBadge}</span>
        </button>

        {/* 右侧：老式电话 */}
        <button
          type="button"
          className={graduated ? 'desk-object' : 'desk-object graybox'}
          style={anchorStyle(MANIFEST.desk.phone)}
          onClick={() => go({ name: 'phone' })}
        >
          <span className="obj-label">{C.objects.phone}</span>
        </button>
        {unread && <span className="phone-lamp" style={anchorStyle(MANIFEST.desk.phoneLamp)} />}

        {/* 最右角落：游戏卡带架 */}
        <button
          type="button"
          className={graduated ? 'desk-object' : 'desk-object graybox'}
          style={anchorStyle(MANIFEST.desk.shelf)}
          onClick={() => go({ name: 'shelf' })}
        >
          <span className="obj-label">{C.objects.shelf}</span>
          <span className="cartridge-row">
            {completed.map((p) => (
              <span key={p.id} className="cartridge-mini" />
            ))}
          </span>
        </button>
      </div>
    </div>
  )
}

function hasUnreadEvents(projectId: string | null): boolean {
  const events = replayEvents(projectId)
  if (events.length === 0) return false
  const maxSeq = events[events.length - 1].seq
  return maxSeq > lastReadSeq(projectId ?? '_global')
}
